import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box,Button, CircularProgress, Typography, Alert } from "@mui/material";

const SSOCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Get query parameters from URL
        const params = new URLSearchParams(location.search);
        const token = params.get("token");
        const role = params.get("role");
        const authProvider = params.get("auth_provider");

        // Check for error in URL
        const errorParam = params.get("error");
        if (errorParam) {
          setError(errorParam);
          setLoading(false);
          return;
        }

        // Validate token exists
        if (!token) {
          setError("No authentication token received");
          setLoading(false);
          return;
        }

        // Save to localStorage
        localStorage.setItem("token", token);
        if (role) {
          localStorage.setItem("role", role);
        }
        if (authProvider) {
          localStorage.setItem("authProvider", authProvider);
        }

        // Small delay to ensure localStorage is set
        await new Promise(resolve => setTimeout(resolve, 100));

        // Redirect based on role
        if (role === "admin") {
          navigate("/companydetails");
        } else {
          navigate("/home");
        }

      } catch (error) {
        //console.error("SSO Callback Error:", error);
        setError("Failed to complete SSO login");
        setLoading(false);
      }
    };

    handleCallback();
  }, [location, navigate]);

  // Show loading or error
  return (
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        p: 3,
      }}
    >
      {loading && !error && (
        <>
          <CircularProgress size={48} sx={{ mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Completing SSO login...
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Please wait while we verify your identity
          </Typography>
        </>
      )}

      {error && (
        <Box sx={{ maxWidth: 500, width: '100%' }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            SSO Login Failed
          </Alert>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {error === 'no_authorization_code' && 'No authorization code received from provider'}
            {error === 'invalid_state' && 'Invalid state parameter. Please try again.'}
            {error === 'session_expired' && 'Session expired. Please try again.'}
            {error === 'email_mismatch' && 'Email from provider does not match. Please try again.'}
            {error === 'user_not_found' && 'User not found. Please contact your admin.'}
            {error === 'sso_callback_failed' && 'SSO callback failed. Please try again.'}
            {error === 'google_auth_failed' && 'Google authentication failed. Please try again.'}
            {error === 'microsoft_auth_failed' && 'Microsoft authentication failed. Please try again.'}
            {!['no_authorization_code', 'invalid_state', 'session_expired', 'email_mismatch', 'user_not_found', 'sso_callback_failed', 'google_auth_failed', 'microsoft_auth_failed'].includes(error) && 
              error
            }
          </Typography>
          <Button 
            variant="contained" 
            onClick={() => navigate("/login")}
            sx={{ mt: 2 }}
          >
            Back to Login
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default SSOCallback;